import { LinearProgress } from '@mui/material';
import { AgChartOptions } from 'ag-charts-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActivitiesByDatesQuery } from '../../../../hooks/queries/useActivitiesQuery';
import { useReportingReportsByUserQuery, useReportingReportsQuery } from '../../../../hooks/queries/useReportingQuery';
import { formatName } from '../../../../utils/capitalize';
import { activityStateEnum, stepModes } from '../../../../utils/enums';
import GenericAgChart from '../../../charts/GenericAgChart';

interface ReportingReportsProps {
    startDate: Date;
    endDate: Date;
}

const ReportingReports: React.FC<ReportingReportsProps> = ({ startDate, endDate }) => {
    const { t } = useTranslation();
    const { data: dataReportingReports, isLoading: isReportingReportsLoading } = useReportingReportsQuery({
        params: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
    });

    const [selectedUser, setSelectedUser] = useState<{ name: string; id: string } | null>(null);
    const [selectedDate, setSelectedDate] = useState('');

    const { data: dataReportingReportsByUser, isLoading: isReportingReportsByUserLoading } = useReportingReportsByUserQuery({
        params: { startDate: startDate.toISOString(), endDate: endDate.toISOString(), userId: selectedUser?.id || '' },
    });

    const { data: dataActivities, isLoading: isActivitiesLoading } = useActivitiesByDatesQuery({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        minState: activityStateEnum.REPORT,
        maxState: activityStateEnum.REPORT,
        stepMode: stepModes.REPORT,
        userId: selectedUser?.id || '',
    });

    const processedData = useMemo(() => {
        return dataReportingReports
            ?.filter((reporting) => !reporting.user.isSpecial)
            .sort((a, b) => (a.user.firstName + a.user.lastName).localeCompare(b.user.firstName + b.user.lastName))
            .map((reporting) => ({
                name: formatName(reporting.user.firstName, reporting.user.lastName),
                username: reporting.user.username.toUpperCase(),
                nbReportsSent: reporting.nbReportsSent,
                nbReportsNotSent: reporting.nbReports - reporting.nbReportsSent,
                nbReports: reporting.nbReports,
                id: reporting.user._id,
                nbActivitiesNotSent: reporting.nbActivities - reporting.nbActivitiesSent,
                nbActivitiesSent: reporting.nbActivitiesSent,
                nbActivities: reporting.nbActivities,
            }));
    }, [dataReportingReports]);

    const options: AgChartOptions = {
        data: processedData,
        title: {
            text: 'Rapports envoyés par utilisateur',
        },
        subtitle: {
            text: 'Comparaison des rapports envoyés et des activités non envoyées',
        },
        zoom: {
            enabled: true,
            axes: 'xy',
            anchorPointX: 'pointer',
            anchorPointY: 'pointer',
            enableAxisDragging: true,
            enablePanning: true,
            enableScrolling: true,
            enableSelecting: true,
            enableTwoFingerZoom: true,
        },
        series: [
            {
                type: 'bar',
                xKey: 'name',
                yKey: 'nbReportsSent',
                yName: 'Rapports envoyés',
                stacked: true,
                stackGroup: 'reports',
            },
            {
                type: 'bar',
                xKey: 'name',
                yKey: 'nbReportsNotSent',
                yName: 'Rapports non envoyés',
                stacked: true,
                stackGroup: 'reports',
            },
            {
                type: 'bar',
                xKey: 'name',
                yKey: 'nbActivitiesSent',
                yName: 'Activités envoyées',
                stacked: true,
                stackGroup: 'activities',
            },
            {
                type: 'bar',
                xKey: 'name',
                yKey: 'nbActivitiesNotSent',
                yName: 'Activités non envoyées',
                stacked: true,
                stackGroup: 'activities',
            },
        ],
        axes: [
            {
                type: 'category',
                position: 'bottom',
                title: {
                    text: 'Utilisateur',
                },
            },
            {
                type: 'number',
                position: 'left',
                title: {
                    text: 'Quantité',
                },
            },
        ],
        legend: {
            position: 'top',
        },
        height: 500,
    };

    return (
        <div className="fx-c gap-5px">
            <GenericAgChart options={options} />
            <div className="bg-black text-white border-radius-10px fx-c pad-15px gap-15px">
                {isReportingReportsLoading && <LinearProgress color="inherit" />}
            </div>
            {/*<div className="fx-c of-y-auto border-radius-10px gap-10px fx-fg-1">
                <div className="dashboard">
                    <ResponsiveCard title="Rapports" heightSize="small" widthSize="xxlarge">
                        <ResponsiveContainer>
                            <BarChart
                                title=""
                                data={processedData}
                                onClick={(nextState, event) => {
                                    if (nextState.activePayload?.at(0).payload.id) {
                                        setSelectedUser({
                                            id: nextState.activePayload?.at(0).payload.id || '',
                                            name: nextState.activePayload?.at(0).payload.name || '',
                                        });
                                        setSelectedDate('');
                                    }
                                }}
                            >
                                <XAxis dataKey={'username'} name={'name'} angle={-90} dy={10} dx={-4} tickLine={false} height={40} />
                                <YAxis
                                    dataKey={'reports'}
                                    domain={[0, getDaysDifference(startDate, endDate) + 1]}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    labelFormatter={(label, payload) => {
                                        const dataPoint = payload?.[0]?.payload;
                                        return dataPoint?.name || label;
                                    }}
                                />
                                <Legend verticalAlign="top" />
                                <CartesianGrid vertical={false} />
                                {processedData && processedData?.length > 30 && <Brush dataKey="username" height={20} stroke="#8884d8" />}
                                <Bar
                                    dataKey="reportsSent"
                                    fill="#55AA55"
                                    stackId={'a'}
                                    name={capitalizeFirstLetter(t('reportsSent'))}
                                    activeBar={<Rectangle fill="purple" stroke="#55AA55" />}
                                />
                                <Bar
                                    dataKey="reportsNotSent"
                                    fill="orange"
                                    stackId={'a'}
                                    name={capitalizeFirstLetter(t('reportsNotSent'))}
                                    activeBar={<Rectangle fill="pink" stroke="orange" />}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                        {isActivitiesLoading && <LinearProgress color="inherit" />}
                    </ResponsiveCard>
                </div>

                {!!dataReportingReportsByUser?.length && (
                    <div className="dashboard">
                        <ResponsiveCard
                            title={capitalizeFirstLetter(t('activities')) + ' : ' + selectedUser?.name}
                            heightSize="small"
                            widthSize="medium"
                        >
                            <ResponsiveContainer>
                                <BarChart
                                    data={dataReportingReportsByUser.map((report) => ({
                                        ...report,
                                        date: new Date(report.date)
                                            .toLocaleDateString(t('localeDate'), {
                                                month: 'numeric',
                                                day: 'numeric',
                                            })
                                            .replace(/\b\w/g, (char) => char.toUpperCase()),
                                        nbActivitiesNotSent: report.nbActivities - report.nbActivitiesSent,
                                        isoDate: report.date,
                                    }))}
                                    onClick={(nextState, event) => {
                                        if (nextState.activePayload?.at(0).payload.isoDate) {
                                            setSelectedDate(nextState.activePayload?.at(0).payload.isoDate || '');
                                        }
                                    }}
                                >
                                    <XAxis dataKey="date" />
                                    <YAxis dataKey={'nbActivities'} axisLine={false} tickLine={false} />
                                    <Tooltip />
                                    <Legend />
                                    <CartesianGrid vertical={false} />
                                    <Bar
                                        dataKey="nbActivitiesSent"
                                        name={capitalizeFirstLetter(t('activitiesSent'))}
                                        fill="#55AA55"
                                        stackId={'b'}
                                        activeBar={<Rectangle fill="purple" stroke="#55AA55" />}
                                    />
                                    <Bar
                                        dataKey="nbActivitiesNotSent"
                                        name={capitalizeFirstLetter(t('activitiesNotSent'))}
                                        fill="orange"
                                        stackId={'b'}
                                        activeBar={<Rectangle fill="pink" stroke="orange" />}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ResponsiveCard>
                        {!!selectedDate.length &&
                            !!dataActivities?.filter((value) => sameDay(new Date(value.reportId.date), new Date(selectedDate))).length && (
                                <ResponsiveCard
                                    title={
                                        capitalizeFirstLetter(t('activitiesNotValidated')) +
                                        ' : ' +
                                        new Date(selectedDate).toLocaleDateString(t('localeDate'), {
                                            month: 'numeric',
                                            day: 'numeric',
                                        })
                                    }
                                    heightSize="small"
                                    widthSize="medium"
                                >
                                    <div className="table-container">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th className="w-100px">{capitalizeFirstLetter(t('date'))}</th>
                                                    <th className="w-200px">{capitalizeFirstLetter(t('site'))}</th>

                                                    <th className="w-300px">{capitalizeFirstLetter(t('observations'))}</th>

                                                    <th className="w-200px">{capitalizeFirstLetter(t('activity'))}</th>
                                                </tr>
                                            </thead>
                                            <tbody id="fu-planning-obj">
                                                {dataActivities
                                                    ?.filter((value) => sameDay(new Date(value.reportId.date), new Date(selectedDate)))
                                                    .map((activity, index, array) => {
                                                        return (
                                                            <tr key={activity._id}>
                                                                <td>
                                                                    {new Date(activity.reportId.date).toLocaleDateString(t('localeDate'), {
                                                                        year: 'numeric',
                                                                        month: 'numeric',
                                                                        day: 'numeric',
                                                                    })}
                                                                </td>
                                                                <td className="pos-sy">
                                                                    <SiteCell
                                                                        activities={array}
                                                                        activity={activity}
                                                                        activityIndex={index}
                                                                        displayAll
                                                                        disabled
                                                                    />
                                                                </td>

                                                                <td>
                                                                    <div>{activity.observationsWorker}</div>
                                                                </td>

                                                                <td>
                                                                    <div>{activity.activityTypeId.designation}</div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                </ResponsiveCard>
                            )}
                    </div>
                )}
            </div>*/}
        </div>
    );
};

export default ReportingReports;
